import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus, Briefcase, Calendar, CheckCircle2, Archive, Loader2, X, Sparkles,
  Copy, Download, TrendingUp, TrendingDown, Minus, BarChart3,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useWorkspace } from "@/lib/workspace-context";

const TABS = [
  { key: "active", label: "Active", icon: Briefcase },
  { key: "completed", label: "Completed", icon: CheckCircle2 },
  { key: "archived", label: "Archived", icon: Archive },
];

export default function InterviewPortfolio() {
  const { workspaces, refresh, premium, canCreateMore, setActive, activeId, loading } = useWorkspace();
  const [tab, setTab] = useState("active");
  const [showCreate, setShowCreate] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const filtered = workspaces.filter((w) => w.status === tab);

  const openCreate = () => {
    if (!canCreateMore) { setShowPaywall(true); return; }
    setShowCreate(true);
  };

  return (
    <div data-testid="portfolio-page" className="animate-in space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">my interviews</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-50">My Interviews</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Each interview you&apos;re preparing for is its own space with its own scores, journey, briefs, and history.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="portfolio-create-btn"
          className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2.5 text-sm shadow-[0_0_24px_rgba(245,158,11,0.28)] transition-all hover:-translate-y-0.5"
        >
          <Plus className="h-4 w-4" /> Prepare for another interview
        </button>
      </div>

      {/* Tabs, always visible */}
      <div className="flex items-center gap-1 border-b border-zinc-800/60">
        {TABS.map(({ key, label, icon: Icon }) => {
          const isActive = tab === key;
          const count = workspaces.filter((w) => w.status === key).length;
          return (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`portfolio-tab-${key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition-colors -mb-px ${
                isActive
                  ? "border-amber-500 text-zinc-50 font-semibold"
                  : "border-transparent text-zinc-500 hover:text-zinc-200"
              }`}
            >
              <Icon className="h-3.5 w-3.5" /> {label}
              <span className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded ${isActive ? "bg-amber-500/15 text-amber-300" : "bg-zinc-800 text-zinc-500"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div data-testid={`portfolio-empty-${tab}`} className="p-8 rounded-xl border border-zinc-800/70 bg-zinc-900/40 text-sm text-zinc-500 text-center">
          No {tab} interviews yet.
        </div>
      ) : (
        <ul data-testid={`portfolio-list-${tab}`} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map((w) => (
            <WorkspaceCard
              key={w.workspace_id}
              w={w}
              isActive={w.workspace_id === activeId}
              canCreateMore={canCreateMore}
              onSelect={() => setActive(w.workspace_id)}
              onChanged={refresh}
            />
          ))}
        </ul>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={async (ws) => { setShowCreate(false); await refresh(); setActive(ws.workspace_id); }} />}
      {showPaywall && <PaywallModal onClose={() => setShowPaywall(false)} />}

      {!premium && (
        <div className="text-[11px] text-zinc-500 pt-4 border-t border-zinc-800/60">
          Free plan · 1 interview. <Link to="/app/account/billing" className="text-amber-400 hover:text-amber-300 underline underline-offset-4">Upgrade to Career Pro</Link> for unlimited interviews.
        </div>
      )}
    </div>
  );
}

function WorkspaceCard({ w, isActive, onSelect, onChanged, canCreateMore }) {
  const counts = w.counts || {};
  const [busy, setBusy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [showDuplicate, setShowDuplicate] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${w.workspace_id}/analytics`);
        if (alive) setAnalytics(data);
      } catch {
        if (alive) setAnalytics(null);
      } finally {
        if (alive) setAnalyticsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [w.workspace_id]);

  const mutate = async (patch) => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${w.workspace_id}`, patch);
      await onChanged();
    } finally { setBusy(false); }
  };

  const archive = async () => {
    if (w.is_default) return;
    setBusy(true);
    try {
      await api.delete(`/workspaces/${w.workspace_id}`);
      await onChanged();
    } finally { setBusy(false); }
  };

  const exportInterview = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${w.workspace_id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (w.name || "interview").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      a.download = `intuiterview-${safe}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };

  return (
    <li
      data-testid={`workspace-card-${w.workspace_id}`}
      className={`relative rounded-xl border p-5 transition-colors ${
        isActive
          ? "border-amber-500/40 bg-amber-500/[0.03] shadow-[0_0_24px_-8px_rgba(245,158,11,0.35)]"
          : "border-zinc-800/70 bg-zinc-900/40 hover:border-zinc-700"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{w.role || "Role"}</div>
            {isActive && <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 bg-amber-500/[0.06] rounded-full px-1.5 py-0.5">Active</span>}
          </div>
          <div className="text-base font-semibold text-zinc-50 truncate" data-testid={`workspace-name-${w.workspace_id}`}>{w.name}</div>
          {w.company && <div className="text-xs text-zinc-500 mt-0.5 truncate">{w.company}</div>}
        </div>
      </div>

      <InterviewDateEditor
        workspaceId={w.workspace_id}
        interviewDate={w.interview_date}
        onChanged={onChanged}
      />

      {/* Analytics tile */}
      <AnalyticsTile analytics={analytics} loading={analyticsLoading} workspaceId={w.workspace_id} />

      <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-zinc-800/60">
        <Stat label="Aligned" value={counts.aligned || 0} />
        <Stat label="Questions" value={counts.questions || 0} />
        <Stat label="Mocks" value={counts.mocks || 0} />
        <Stat label="Briefs" value={counts.briefs || 0} />
      </div>

      <div className="flex items-center justify-between gap-2 mt-4 pt-3 border-t border-zinc-800/60">
        <button
          onClick={onSelect}
          data-testid={`workspace-select-${w.workspace_id}`}
          disabled={isActive}
          className={`inline-flex items-center gap-1.5 text-xs font-semibold rounded-md px-3 py-1.5 transition-colors ${
            isActive
              ? "bg-amber-500/10 text-amber-300 border border-amber-500/25 cursor-default"
              : "bg-zinc-800 hover:bg-amber-500 hover:text-amber-950 text-zinc-100"
          }`}
        >
          {isActive ? "Selected" : "Open"}
        </button>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowDuplicate(true)}
            disabled={busy}
            data-testid={`workspace-duplicate-${w.workspace_id}`}
            title="Duplicate for another role/company"
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 px-2 py-1 transition-colors inline-flex items-center gap-1"
          >
            <Copy className="h-3 w-3" /> Duplicate
          </button>
          <button
            onClick={exportInterview}
            disabled={busy}
            data-testid={`workspace-export-${w.workspace_id}`}
            title="Download as JSON"
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 px-2 py-1 transition-colors inline-flex items-center gap-1"
          >
            <Download className="h-3 w-3" /> Export
          </button>
          {w.status === "active" && (
            <button
              onClick={() => mutate({ status: "completed" })}
              disabled={busy}
              data-testid={`workspace-complete-${w.workspace_id}`}
              title="Mark completed"
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-emerald-400 px-2 py-1 transition-colors"
            >Complete</button>
          )}
          {w.status === "completed" && (
            <button
              onClick={() => mutate({ status: "active" })}
              disabled={busy}
              data-testid={`workspace-reactivate-${w.workspace_id}`}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 px-2 py-1 transition-colors"
            >Reactivate</button>
          )}
          {w.status === "archived" && (
            <button
              onClick={() => mutate({ status: "active" })}
              disabled={busy}
              data-testid={`workspace-restore-${w.workspace_id}`}
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 px-2 py-1 transition-colors"
            >Restore</button>
          )}
          {!w.is_default && w.status !== "archived" && (
            <button
              onClick={archive}
              disabled={busy}
              data-testid={`workspace-archive-${w.workspace_id}`}
              title="Archive"
              className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-rose-300 px-2 py-1 transition-colors"
            >Archive</button>
          )}
        </div>
      </div>

      {showDuplicate && (
        <DuplicateModal
          source={w}
          canCreateMore={canCreateMore}
          onClose={() => setShowDuplicate(false)}
          onDone={async () => { setShowDuplicate(false); await onChanged(); }}
        />
      )}
    </li>
  );
}

/**
 * Inline interview-date editor on each workspace card. Reads the date-time
 * value, converts to the `datetime-local` string shape on edit, PATCHes the
 * workspace on save, and re-fetches so the card immediately reflects the new
 * value. Users can also clear the date entirely.
 */
function InterviewDateEditor({ workspaceId, interviewDate, onChanged }) {
  const initial = interviewDate ? interviewDate.slice(0, 16) : "";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { setValue(initial); }, [initial]);

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      await api.patch(`/workspaces/${workspaceId}`, { interview_date: value || null });
      await onChanged();
      setEditing(false);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${workspaceId}`, { interview_date: null });
      setValue("");
      await onChanged();
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        data-testid={`workspace-date-edit-${workspaceId}`}
        className="text-[11px] text-zinc-400 hover:text-amber-300 inline-flex items-center gap-1.5 mb-3 -mx-1 px-1 py-0.5 rounded transition-colors"
      >
        <Calendar className="h-3 w-3" />
        {interviewDate ? (
          <>
            <span>Interview: {new Date(interviewDate).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
            <span className="text-zinc-600">· edit</span>
          </>
        ) : (
          <span className="text-zinc-500">Set interview date & time</span>
        )}
      </button>
    );
  }

  return (
    <div
      data-testid={`workspace-date-editor-${workspaceId}`}
      className="mb-3 rounded-lg border border-zinc-800/70 bg-zinc-950/60 p-2.5 flex flex-wrap items-center gap-2"
    >
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`workspace-date-input-${workspaceId}`}
        className="bg-zinc-900 border border-zinc-800 text-zinc-100 text-xs rounded-md px-2 py-1.5 focus:outline-none focus:border-amber-500/40 flex-1 min-w-[10rem]"
      />
      <button
        onClick={save}
        disabled={saving}
        data-testid={`workspace-date-save-${workspaceId}`}
        className="text-[11px] font-semibold bg-amber-500 hover:bg-amber-400 text-amber-950 rounded-md px-3 py-1.5 disabled:opacity-60 inline-flex items-center gap-1"
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        Save
      </button>
      {interviewDate && (
        <button
          onClick={clear}
          disabled={saving}
          data-testid={`workspace-date-clear-${workspaceId}`}
          className="text-[11px] text-zinc-400 hover:text-rose-300 px-2 py-1.5 transition-colors"
        >
          Clear
        </button>
      )}
      <button
        onClick={() => { setEditing(false); setValue(initial); setErr(""); }}
        disabled={saving}
        data-testid={`workspace-date-cancel-${workspaceId}`}
        className="text-[11px] text-zinc-500 hover:text-zinc-200 px-2 py-1.5 transition-colors"
      >
        Cancel
      </button>
      {err && <div className="basis-full text-[10px] text-rose-300">{err}</div>}
    </div>
  );
}

/**
 * Compact analytics tile shown inside each workspace card. Surfaces the current
 * readiness, its delta since the first mock, and a 14-day activity sparkline.
 */
function AnalyticsTile({ analytics, loading, workspaceId }) {
  if (loading) {
    return (
      <div
        data-testid={`workspace-analytics-loading-${workspaceId}`}
        className="mt-3 rounded-lg border border-zinc-800/50 bg-zinc-950/40 px-3 py-2.5 text-[10px] text-zinc-600 inline-flex items-center gap-2"
      >
        <Loader2 className="h-3 w-3 animate-spin" /> Loading progress…
      </div>
    );
  }
  if (!analytics || (analytics.totals?.mocks === 0 && (analytics.match_trend?.length || 0) === 0)) {
    return (
      <div
        data-testid={`workspace-analytics-empty-${workspaceId}`}
        className="mt-3 rounded-lg border border-dashed border-zinc-800/70 bg-zinc-950/30 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-600 inline-flex items-center gap-2"
      >
        <BarChart3 className="h-3 w-3" /> No practice yet
      </div>
    );
  }

  const readiness = analytics.latest_readiness;
  const delta = analytics.readiness_delta;
  const activity = analytics.activity || [];
  const maxCount = Math.max(1, ...activity.map((a) => (a.mocks || 0) + (a.briefs || 0) + (a.aligned || 0)));

  const TrendIcon = delta == null ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const trendColor = delta == null ? "text-zinc-500" : delta > 0 ? "text-emerald-400" : delta < 0 ? "text-rose-300" : "text-zinc-500";

  return (
    <div
      data-testid={`workspace-analytics-${workspaceId}`}
      className="mt-3 rounded-lg border border-zinc-800/60 bg-zinc-950/40 px-3 py-2.5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-zinc-500">Readiness</div>
            <div className="text-lg font-semibold text-zinc-100 tabular-nums leading-tight">
              {readiness == null ? ", " : `${readiness}%`}
            </div>
          </div>
          {delta != null && (
            <div className={`inline-flex items-center gap-1 text-[10px] font-medium ${trendColor}`}>
              <TrendIcon className="h-3 w-3" />
              {delta > 0 ? "+" : ""}{delta}
            </div>
          )}
          {analytics.best_mock && (
            <div className="ml-2 hidden sm:block">
              <div className="text-[9px] uppercase tracking-widest text-zinc-500">Best mock</div>
              <div className="text-xs text-zinc-200 tabular-nums leading-tight">{analytics.best_mock.score}%</div>
            </div>
          )}
        </div>
        {/* Sparkline (14-day activity) */}
        <div className="flex items-end gap-[2px] h-6" aria-hidden>
          {(activity.length ? activity : Array(7).fill({ mocks: 0, briefs: 0, aligned: 0 })).slice(-14).map((d, i) => {
            const total = (d.mocks || 0) + (d.briefs || 0) + (d.aligned || 0);
            const h = Math.max(2, Math.round((total / maxCount) * 22));
            const activeDay = total > 0;
            return (
              <span
                key={i}
                title={d.date ? `${d.date}: ${total}` : ""}
                style={{ height: `${h}px` }}
                className={`w-[3px] rounded-sm ${activeDay ? "bg-amber-500/80" : "bg-zinc-800"}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DuplicateModal({ source, canCreateMore, onClose, onDone }) {
  const [role, setRole] = useState(source.role || "");
  const [company, setCompany] = useState(source.company || "");
  const [name, setName] = useState("");
  const [includeResumes, setIncludeResumes] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    if (!canCreateMore) {
      setErr("You've reached your interview limit. Upgrade to Career Pro for unlimited interviews.");
      return;
    }
    setErr("");
    setBusy(true);
    try {
      await api.post(`/workspaces/${source.workspace_id}/duplicate`, {
        name: name || null,
        role: role || null,
        company: company || null,
        include_resumes: includeResumes,
      });
      onDone();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not duplicate.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} testid="workspace-duplicate-modal">
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">duplicate interview</div>
      <h2 className="text-xl font-bold text-zinc-50 mb-1">Prepare for a similar interview</h2>
      <p className="text-sm text-zinc-400 mb-4">
        Copies role, company, JD, and (optionally) your resumes. You&apos;ll start with a clean readiness score and journey.
      </p>
      <div className="space-y-3">
        <Field label="Role (edit as needed)">
          <input value={role} onChange={(e) => setRole(e.target.value)}
            data-testid="duplicate-role"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700" />
        </Field>
        <Field label="Company (edit as needed)">
          <input value={company} onChange={(e) => setCompany(e.target.value)}
            data-testid="duplicate-company"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700" />
        </Field>
        <Field label="Name (optional, auto-filled)">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${role || "Role"} @ ${company || "Company"}`}
            data-testid="duplicate-name"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700" />
        </Field>
        <label className="flex items-center gap-2 text-xs text-zinc-300 pt-1">
          <input
            type="checkbox"
            checked={includeResumes}
            onChange={(e) => setIncludeResumes(e.target.checked)}
            data-testid="duplicate-include-resumes"
            className="h-3.5 w-3.5 rounded border-zinc-700 bg-zinc-950 accent-amber-500"
          />
          Also copy my resumes ({source?.counts?.resumes || 0}) into the new interview
        </label>
      </div>
      {err && <div className="text-xs text-rose-300 mt-3">{err}</div>}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="text-sm border border-zinc-800 text-zinc-400 rounded-lg px-4 py-2">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="duplicate-submit"
          className="text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Copy className="h-3.5 w-3.5" />}
          Duplicate
        </button>
      </div>
    </ModalShell>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-100 font-semibold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [company, setCompany] = useState("");
  const [interviewDate, setInterviewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.post("/workspaces", {
        name: name || null,
        role: role || null,
        company: company || null,
        interview_date: interviewDate || null,
      });
      onCreated(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Could not create workspace.");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} testid="workspace-create-modal">
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">new interview</div>
      <h2 className="text-xl font-bold text-zinc-50 mb-4">Prepare for another interview</h2>
      <div className="space-y-3">
        <Field label="Name (optional, auto-filled from role & company)">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Senior PM @ Stripe"
            data-testid="create-name"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700" />
        </Field>
        <Field label="Role">
          <input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Senior Product Manager"
            data-testid="create-role"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700" />
        </Field>
        <Field label="Company">
          <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Stripe"
            data-testid="create-company"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700" />
        </Field>
        <Field label="Interview date (optional)">
          <input type="datetime-local" value={interviewDate} onChange={(e) => setInterviewDate(e.target.value)}
            data-testid="create-date"
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700" />
        </Field>
      </div>
      {err && <div className="text-xs text-rose-300 mt-3">{err}</div>}
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="text-sm border border-zinc-800 text-zinc-400 rounded-lg px-4 py-2">Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="create-submit"
          className="text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 disabled:opacity-60 inline-flex items-center gap-2">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Create
        </button>
      </div>
    </ModalShell>
  );
}

function PaywallModal({ onClose }) {
  return (
    <ModalShell onClose={onClose} testid="workspace-paywall-modal">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-amber-400 mb-2">
        <Sparkles className="h-3 w-3" /> Career Pro
      </div>
      <h2 className="text-xl font-bold text-zinc-50 mb-2">Unlock unlimited interviews</h2>
      <p className="text-sm text-zinc-400 leading-relaxed">
        Your free plan supports one active interview. Upgrade to Career Pro to prep for multiple interviews in parallel, each with its own journey, briefs, and interview memory.
      </p>
      <div className="flex justify-end gap-2 mt-5">
        <button onClick={onClose} className="text-sm border border-zinc-800 text-zinc-400 rounded-lg px-4 py-2">Not now</button>
        <Link to="/app/account/billing" data-testid="paywall-upgrade-cta"
          className="text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Upgrade to Career Pro
        </Link>
      </div>
    </ModalShell>
  );
}

function ModalShell({ children, onClose, testid }) {
  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 animate-in"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-zinc-800/70 bg-zinc-950 p-6 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-100 transition-colors">
          <X className="h-4 w-4" />
        </button>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
